import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { routerPaths } from "@/app/routerPaths";
import { isDev } from "@/shared/config";
import { UIKit } from "@/shared/ui";
import {
	fabricTextureById,
	findKitchenDesign,
	getCatalogItem,
	isUpholstered,
	kitchenPieces,
	KITCHEN_PIECE_SEPARATOR,
	sectionOfItem,
	splitKitchenPieceId,
	type CatalogItem,
} from "@/shared/catalog";
import { floorItemOwner, floorItemsStoreFor } from "@/shared/store/layers";
import { DrawerSection, SelectionKind, ViewMode, useEditorStore } from "@/widgets/editor";
import { useProjectStore } from "@/shared/store";
import { remove } from "@/shared/store/actions";
import { CatalogSectionKind, itemDimensions, resizeFloorItem, type FloorItem } from "@/shared/plan";
import { findFloorItem } from "@/shared/plan";
import { ItemSizeFields } from "./ItemSizeFields";
import { ObjectComments } from "./ObjectComments";
import { VariantsStrip } from "@/widgets/place-fixture";

/**
 * Разделить поставленную кухню на части: каждая сторона и остров становятся отдельными
 * предметами на тех же местах (с учётом поворота кухни), а сама кухня уходит. Одним шагом
 * истории: Ctrl+Z собирает кухню обратно.
 */
const splitKitchen = (item: FloorItem) => {
	const kitchen = findKitchenDesign(item.ref.item);
	if (!kitchen) return;
	const angle = (item.rotation * Math.PI) / 180;
	const cos = Math.cos(angle);
	const sin = Math.sin(angle);
	const store = floorItemsStoreFor(item.ref.catalog);
	useProjectStore.getState().recordEdit(() => {
		for (const piece of kitchenPieces(kitchen)) {
			const [cx, cz] = piece.center;
			const ref = {
				catalog: item.ref.catalog,
				item: `${kitchen.id}${KITCHEN_PIECE_SEPARATOR}${piece.run}`,
			};
			store
				.getState()
				.addFloorItem(
					ref,
					item.x + cx * cos - cz * sin,
					item.y + cx * sin + cz * cos,
					item.rotation,
				);
		}
		remove({ kind: SelectionKind.Item, id: item.id });
	});
};

/** Есть что делить: кухня из конструктора, у которой больше одной части */
const canSplitKitchen = (id: string) => {
	const kitchen = findKitchenDesign(id);
	return kitchen !== null && kitchenPieces(kitchen).length > 1;
};

/** Предмет на полу лежит в мебели или в сантехнике — правим через владельца. */
const updateFloorItem = (id: string, patch: Partial<Omit<FloorItem, "id">>) =>
	floorItemOwner(id)?.getState().updateFloorItem(id, patch);

export type PropertiesPanelCatalogItem = CatalogItem;

/** DEV: путь к файлу модели в репозитории — найти и удалить неудачную модель. */
const DevModelFile = ({ info }: { info: CatalogItem }) => {
	const [copied, setCopied] = useState(false);
	const file =
		info.view3d.type === "model"
			? `frontend/public${info.view3d.url}`
			: `${info.view3d.type}: ${info.id}`;

	return (
		<UIKit.Button
			type="button"
			className="rounded-lg border border-dashed border-border bg-surface px-2 py-1.75 text-left font-mono text-xs break-all text-ink hover:border-accent/50"
			title="Скопировать путь к файлу модели"
			onClick={async () => {
				await navigator.clipboard.writeText(file);
				setCopied(true);
				window.setTimeout(() => setCopied(false), 1500);
			}}
		>
			{copied ? "Скопировано" : file}
		</UIKit.Button>
	);
};

export const FloorItemProperties = () => {
	const layers = useProjectStore((s) => s.project.layers);
	const selection = useEditorStore((s) => s.selection);
	const setSelection = useEditorStore((s) => s.setSelection);
	const viewMode = useEditorStore((s) => s.viewMode);
	const pushHistory = useProjectStore((s) => s.pushHistory);
	const navigate = useNavigate();

	// Хелпер для updateFixture в зависимости от каталога

	const onDelete = () => {
		// Удаление кнопкой отменяется по Ctrl+Z, как и клавишей Delete
		pushHistory();
		remove(selection);
		setSelection(null);
	};

	if (!selection) return null;
	if (selection.kind === SelectionKind.Item) {
		const found = findFloorItem(layers, selection.id);
		if (!found) return null;
		const { item } = found;
		const info = getCatalogItem(item.ref);
		const dimensions = info ? itemDimensions(item, info) : null;
		const resizable = sectionOfItem(item.ref)?.canChangeDimensions ?? false;

		return (
			<>
				<UIKit.Text className="text-sm font-semibold tracking-label text-ink uppercase">
					{info?.name ?? "Объект удалён из каталога"}
				</UIKit.Text>
				{viewMode === ViewMode.ThreeD && <VariantsStrip location="properties" />}
				{viewMode === ViewMode.ThreeD && isUpholstered(info) && (
					<UpholsteryButton fabricId={item.fabric} fallbackColor={info?.color} />
				)}
				{isDev && info && <DevModelFile info={info} />}
				{dimensions && (
					<div className="rounded-lg bg-bg px-2.5 py-2 text-xs text-ink">
						{dimensions.width} × {dimensions.depth} мм, высота {dimensions.height} мм
					</div>
				)}
				{info && dimensions && (
					<ItemSizeFields
						info={info}
						dimensions={dimensions}
						disabled={!resizable}
						onResize={(axis, value) => {
							// Глубина растёт вперёд от спинки: шкаф у стены остаётся у стены,
							// а растущий предмет упирается в стены, а не уходит за них
							pushHistory();
							const walls = useProjectStore.getState().project.layers.walls;
							updateFloorItem(item.id, resizeFloorItem(item, info, axis, value, undefined, walls));
						}}
					/>
				)}
				{/* Шкаф из конструктора меняют там же, где собрали: размеры, секции, двери */}
				{info && item.ref.catalog === CatalogSectionKind.CustomFurniture && (
					<UIKit.Button
						type="button"
						className="rounded-lg border border-accent/40 bg-accent-soft px-2 py-1.75 text-xs font-medium text-accent hover:border-accent"
						onClick={() =>
							void navigate(
								`${routerPaths.furnitureGenerator}?id=${encodeURIComponent(splitKitchenPieceId(item.ref.item).kitchenId)}`,
							)
						}
					>
						Изменить в конструкторе
					</UIKit.Button>
				)}
				{/* Кухня ставится целиком; разделённую можно расставлять по частям */}
				{info &&
					item.ref.catalog === CatalogSectionKind.CustomFurniture &&
					!item.ref.item.includes(KITCHEN_PIECE_SEPARATOR) &&
					canSplitKitchen(item.ref.item) && (
						<UIKit.Button
							type="button"
							className="rounded-lg border border-border bg-surface px-2 py-1.75 text-xs text-ink hover:border-accent/50"
							title="Остров и каждая сторона станут отдельными предметами — их можно двигать по одному"
							onClick={() => {
								splitKitchen(item);
								setSelection(null);
							}}
						>
							Разделить на части
						</UIKit.Button>
					)}
				<UIKit.NumberField
					label="Высота от пола"
					value={item.elevation ?? info?.elevation ?? 0}
					min={0}
					step={50}
					onChange={(elevation) => updateFloorItem(item.id, { elevation })}
				/>
				<UIKit.NumberField
					label="Поворот"
					unit="°"
					value={item.rotation}
					min={0}
					max={359}
					step={15}
					onChange={(rotation) => updateFloorItem(item.id, { rotation })}
				/>
				<UIKit.Button
					type="button"
					className="rounded-lg border border-border bg-surface px-2 py-1.75 text-xs text-ink hover:border-accent/50 hover:text-ink"
					title="Повернуть на 90° (R)"
					onClick={() => updateFloorItem(item.id, { rotation: item.rotation + 90 })}
				>
					Повернуть на 90°
				</UIKit.Button>
				<UIKit.Button
					type="button"
					className="rounded-lg border border-border bg-surface px-2 py-1.75 text-xs text-ink hover:border-accent/50 hover:text-ink"
					title="Отразить зеркально слева направо"
					onClick={() =>
						useProjectStore
							.getState()
							.recordEdit(() => updateFloorItem(item.id, { mirrored: !item.mirrored || undefined }))
					}
				>
					Отразить зеркально
				</UIKit.Button>

				<ObjectComments kind="item" id={item.id} />
				<UIKit.Button
					type="button"
					className="mt-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-sm text-danger hover:bg-red-100 dark:border-red-500/50 dark:bg-red-950/30 dark:hover:bg-red-950/45"
					onClick={onDelete}
				>
					Удалить объект
				</UIKit.Button>
			</>
		);
	}

	return null;
};

/** Список типов раздела каталога — сменить тип объекта, не переставляя его. */
export function CatalogTypeList({
	items,
	current,
	onPick,
}: {
	items: CatalogItem[];
	current: string;
	onPick: (itemId: string) => void;
}) {
	if (items.length === 0) return null;
	return (
		<>
			<UIKit.Text
				as="span"
				className="mt-1 text-xs font-semibold tracking-label text-ink uppercase"
			>
				Тип
			</UIKit.Text>
			<div className="flex flex-col gap-1">
				{items.map((item) => (
					<UIKit.Button
						key={item.id}
						type="button"
						className={`flex items-center gap-2 rounded-lg border border-transparent px-2.25 py-1.75 text-left text-sm text-ink hover:bg-[#f3f5f9]${
							current === item.id ? " border-accent bg-accent-soft text-accent" : ""
						}`}
						onClick={() => onPick(item.id)}
					>
						<UIKit.Text
							as="span"
							className="size-2.5 shrink-0 rounded-sm"
							style={{ background: item.color }}
						/>
						<UIKit.Text as="span" className="min-w-0 flex-1">
							{item.name}
						</UIKit.Text>
					</UIKit.Button>
				))}
			</div>
		</>
	);
}

/**
 * Обивка мягкой мебели: образец ткани на предмете и переход в каталог тканей —
 * панель «Отделка» сама покажет ткани и кожу для выделенного дивана.
 */
const UpholsteryButton = ({
	fabricId,
	fallbackColor,
}: {
	fabricId: string | undefined;
	fallbackColor: string | undefined;
}) => {
	const setDrawer = useEditorStore((s) => s.setDrawer);
	const isReadOnly = useEditorStore((s) => s.isReadOnly);
	const fabric = fabricId ? fabricTextureById(fabricId) : null;
	return (
		<UIKit.Button
			type="button"
			disabled={isReadOnly}
			className="flex items-center gap-2 rounded-lg border border-border bg-surface px-2.5 py-2 text-left text-sm text-ink hover:border-accent/50 hover:bg-bg"
			onClick={() => setDrawer(DrawerSection.Finishes)}
		>
			<UIKit.Text
				as="span"
				className="size-7 shrink-0 overflow-hidden rounded-md border border-border"
				style={{ backgroundColor: fabric?.color ?? fallbackColor }}
			>
				{fabric && <img src={fabric.thumb} alt="" className="size-full object-cover" />}
			</UIKit.Text>
			<UIKit.Text as="span" className="min-w-0 flex-1">
				<UIKit.Text as="span" className="block text-xs text-muted">
					Обивка
				</UIKit.Text>
				<UIKit.Text as="span" className="block truncate">
					{fabric?.name ?? "Выбрать ткань или кожу"}
				</UIKit.Text>
			</UIKit.Text>
		</UIKit.Button>
	);
};
